/**
 * Copyright (C) 2025 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */
import { useEffect, useMemo, useRef } from 'react';
import { Box } from '@mui/material';
import circle from '@turf/circle';
import { Feature, FeatureCollection, featureCollection } from '@turf/helpers';
import intersect from '@turf/intersect';
import union from '@turf/union';
import destination from '@turf/destination';
import { MultiPolygon, Polygon } from 'geojson';
import isNil from 'lodash/isNil';
import { shallowEqual, useSelector } from 'react-redux';
import { useRouteMatch } from 'react-router-dom';
import {
  selectAppConfig,
  selectBeams,
  selectBeamsOnMap,
  selectGroundStationCoverage,
  selectGroundStationVisibleStations,
  selectHcxHnaZonesChecked,
  selectHeatMaps,
  selectHeatMapsEnabled,
  selectKaHnaZonesChecked,
  selectKuHnaZonesChecked,
  selectGxHnaZonesChecked,
  selectMapSettingsFtvMapSize,
  selectOverlaysCurrentContext,
  selectRlFlToggleState,
  selectSteerableBeams,
  selectDetailsPanelOpen,
} from '../../../store';
import { SteerableBeamsRing } from '../../../shared/entities/steerableBeam';
import { BeamOnMap, OverlaysContextsEnum, RlFlToggleStateEnum } from '../../../store/maps/reducer';
import MenuItems from '../layout/menu/MenuItems';
import { getMapHeight } from '../map_settings/MapSizeController';
import HeatmapLegendPanel from './HeatmapLegendPanel';
import RlFlToggleSwitch from './RlFlToggleSwitch';
import { useSelectorWithMapId } from './BaseMapContainer';
import { FEATURE_FLAGS, isFeatureEnabled } from '../../../utils/FeatureFlagUtils';
import RulerPath from './RulerPath';
import { GoogleMapsOverlay as DeckOverlay } from '@deck.gl/google-maps';
import { useBeamGeoJsonLayers } from './useBeamGeoJsonLayers';
import GeoJSONMapTooltip, { beamsTooltipTextWithData } from './GeoJSONMapTooltip';
import { useGpsSaPositionAccuracyLayers } from './useGpsSaPositionAccuracyLayers';
import GpsSaPositionAccuracyTooltip from './GpsSaPositionAccuracyTooltip';
import MapOverlayContainer from './MapOverlayContainer';
import { MapType, Network } from '../../../shared/entities/beam';
import { getContourType } from '../../../utils/BeamUtils';

export interface MapOverlaysProps {
  map: google.maps.Map;
  loaded: boolean;
}

const WEBGL_LOSE_CONTEXT = 'WEBGL_lose_context';

const MapOverlays = (props: MapOverlaysProps) => {
  const { loaded, map } = props;
  const beams: FeatureCollection | undefined = useSelector(selectBeams, shallowEqual);
  const groundStationCoverage = useSelector(selectGroundStationCoverage);
  const visibleStations = useSelector(selectGroundStationVisibleStations);
  const heatMaps = useSelector(selectHeatMaps, shallowEqual);
  const steerableBeams = useSelector(selectSteerableBeams, shallowEqual);
  const rlFlToggleState = useSelectorWithMapId(selectRlFlToggleState);
  const mapSettingsFtvMapSize = useSelectorWithMapId(selectMapSettingsFtvMapSize);
  const overlaysCurrentContext = useSelector(selectOverlaysCurrentContext);
  const heatMapsEnabled = useSelectorWithMapId(selectHeatMapsEnabled);
  const beamsOnMap = useSelectorWithMapId(selectBeamsOnMap);
  const kuHnaZonesChecked = useSelectorWithMapId(selectKuHnaZonesChecked);
  const kaHnaZonesChecked = useSelectorWithMapId(selectKaHnaZonesChecked);
  const hcxHnaZonesChecked = useSelectorWithMapId(selectHcxHnaZonesChecked);
  const gxHnaZonesChecked = useSelectorWithMapId(selectGxHnaZonesChecked);
  const appConfig = useSelector(selectAppConfig, shallowEqual);
  const detailsPanelOpen = useSelector(selectDetailsPanelOpen, shallowEqual);
  const isPlansExportView = useRouteMatch(`/${MenuItems.plans}/export`);
  const isTransportMonitoringView = useRouteMatch(`/${MenuItems.transportMonitoring}`);
  const data = useMemo(() => {
    let data: FeatureCollection = featureCollection([]);
    if (beams) {
      data = {
        ...data,
        features: [
          ...beams.features.filter((feature: Feature) =>
            beamsOnMap?.find(
              (beamOnMap: BeamOnMap) =>
                beamOnMap.beamId === feature.properties?.name &&
                beamOnMap.networkType === feature.properties?.network &&
                beamOnMap.mapType === feature.properties?.mapType,
            ),
          ),
          ...steerableBeams.beams
            .filter(
              (steerableBeam: SteerableBeamsRing) =>
                !isNil(steerableBeam.centerLatitudeDeg) &&
                !isNil(steerableBeam.centerLongitudeDeg) &&
                beamsOnMap?.find(
                  (beamOnMap: BeamOnMap) =>
                    beamOnMap.beamId === steerableBeam.beamName &&
                    getContourType(beamOnMap.networkType, beamOnMap.mapType).toString() ===
                      steerableBeam.beamContour?.properties?.contourType,
                ),
            )
            .map((steerableBeam: SteerableBeamsRing) => {
              if (steerableBeam.beamContour) {
                // TODO: Temporarily enrich steerable beam properties for phase 1 contourType refactoring. For Phase 2, add network & mapType to steerable beam API response, and remove the enrichment here.
                if (steerableBeam.beamContour.properties) {
                  steerableBeam.beamContour.properties.network = Network.I5_HCX;
                  steerableBeam.beamContour.properties.mapType = MapType.COVERAGE;
                }
                return steerableBeam.beamContour;
              }
              const hcxCircle = circle(
                [steerableBeam.centerLongitudeDeg, steerableBeam.centerLatitudeDeg],
                steerableBeams.radiusNauticalMiles,

                {
                  steps: 100,
                  units: 'nauticalmiles',
                  properties: {
                    name: steerableBeam.beamName,
                    displayName: steerableBeam.beamName,
                    network: Network.I5_HCX,
                    mapType: MapType.COVERAGE,
                  },
                },
              );
              const hcxFeature = beams.features.find(
                (feature: Feature) => (feature.properties || {}).name === steerableBeam.beamName,
              );

              //Get the left and right most edges of the steerable beam contour
              const leftMostEdgeLng = destination(
                [steerableBeam.centerLongitudeDeg, steerableBeam.centerLatitudeDeg],
                steerableBeams.radiusNauticalMiles,
                -90,
                {
                  units: 'nauticalmiles',
                },
              ).geometry.coordinates[0];
              const rightMostEdgeLng = destination(
                [steerableBeam.centerLongitudeDeg, steerableBeam.centerLatitudeDeg],
                steerableBeams.radiusNauticalMiles,
                90,
                {
                  units: 'nauticalmiles',
                },
              ).geometry.coordinates[0];

              const noTranslation =
                !isNil(leftMostEdgeLng) &&
                !isNil(rightMostEdgeLng) &&
                !isNil(hcxCircle) &&
                !isNil(hcxFeature) &&
                leftMostEdgeLng < 0 &&
                rightMostEdgeLng > 0;

              // Don't translate hcxCircle if it overlaps the meridian, then calculate intersection
              if (noTranslation) {
                const beamAndFeatureIntersection = intersect(hcxCircle, hcxFeature as Feature<MultiPolygon>);
                return { ...beamAndFeatureIntersection, properties: (hcxFeature || {}).properties };
              }

              // Apply the translation, then calculate intersection
              const translation = steerableBeam.centerLongitudeDeg < 0 ? 360 : -360;

              const hcxCircleTranslated360 = circle(
                [steerableBeam.centerLongitudeDeg + translation, steerableBeam.centerLatitudeDeg],
                steerableBeams.radiusNauticalMiles,

                {
                  steps: 100,
                  units: 'nauticalmiles',
                  properties: {
                    name: steerableBeam.beamName,
                    displayName: steerableBeam.beamName,
                    network: Network.I5_HCX,
                    mapType: MapType.COVERAGE,
                  },
                },
              );

              if (hcxCircle && hcxFeature && hcxCircleTranslated360) {
                const normalIntersect = intersect(hcxCircle, hcxFeature as Feature<MultiPolygon>);
                const translatedIntersect = intersect(hcxCircleTranslated360, hcxFeature as Feature<MultiPolygon>);

                if (translatedIntersect) {
                  const unioned = union(normalIntersect, translatedIntersect);
                  return { ...unioned, properties: hcxFeature.properties };
                }

                return { ...normalIntersect, properties: (hcxFeature || {}).properties };
              } else if (hcxFeature) {
                return { ...hcxCircle, properties: (hcxFeature || {}).properties };
              }
              return undefined;
            })
            .filter((feature: Feature) => feature),
        ],
      };
    }

    if (groundStationCoverage?.features && isTransportMonitoringView) {
      const rteCoverage = groundStationCoverage.features
        .filter((f: any) => f && f.properties && visibleStations[f.properties?.siteDesignation])
        .map((f: any) => ({
          ...f,
          properties: {
            ...f.properties,
            displayName: f.properties?.displayName ?? f.properties?.siteDesignation ?? 'Ground Station',
            name: f.properties?.siteDesignation,
            mapType: MapType.COVERAGE,
            network: Network.RTE,
          },
        }));

      data = {
        ...data,
        features: [
          ...data.features,
          ...rteCoverage,
        ]
      };
    }

    if (heatMapsEnabled && heatMaps.flHeatMapContours && rlFlToggleState === RlFlToggleStateEnum.ForwardLink) {
      data = {
        ...data,
        features: [
          ...data.features,
          ...heatMaps.flHeatMapContours.features.filter((feature: Feature) =>
            beamsOnMap?.find(
              (beamOnMap: BeamOnMap) =>
                beamOnMap.beamId === feature.properties?.beamName &&
                beamOnMap.networkType === feature.properties?.network,
            ),
          ),
        ],
      };
    }
    if (heatMapsEnabled && heatMaps.rlHeatMapContours && rlFlToggleState === RlFlToggleStateEnum.ReturnLink) {
      data = {
        ...data,
        features: [
          ...data.features,
          ...heatMaps.rlHeatMapContours.features.filter((feature: Feature) =>
            beamsOnMap?.find(
              (beamOnMap: BeamOnMap) =>
                beamOnMap.beamId === feature.properties?.beamName &&
                beamOnMap.networkType === feature.properties?.network,
            ),
          ),
        ],
      };
    }
    if (beams && kuHnaZonesChecked) {
      data = {
        ...data,
        features: [
          ...data.features,
          ...beams.features.filter(
            (feature: Feature) => feature.properties?.mapType === 'HNA' && feature.properties?.network === 'VGNET',
          ),
        ],
      };
    }
    if (beams && kaHnaZonesChecked && isFeatureEnabled(appConfig, FEATURE_FLAGS.areKaHnaZonesEnabled)) {
      data = {
        ...data,
        features: [
          ...data.features,
          ...beams.features.filter(
            (feature: Feature) => feature.properties?.mapType === 'HNA' && feature.properties?.network === 'VIASAT',
          ),
        ],
      };
    }
    if (beams && hcxHnaZonesChecked && isFeatureEnabled(appConfig, FEATURE_FLAGS.areHcxHnaZonesEnabled)) {
      data = {
        ...data,
        features: [
          ...data.features,
          ...beams.features.filter(
            (feature: Feature) => feature.properties?.mapType === 'HNA' && feature.properties?.network === 'I5_HCX',
          ),
        ],
      };
    }
    if (beams && gxHnaZonesChecked && isFeatureEnabled(appConfig, FEATURE_FLAGS.areGxHnaZonesEnabled)) {
      data = {
        ...data,
        features: [
          ...data.features,
          ...beams.features.filter(
            (feature: Feature) => feature.properties?.mapType === 'HNA' && feature.properties?.network === 'GX',
          ),
        ],
      };
    }
    return data;
  }, [
    appConfig,
    beams,
    groundStationCoverage,
    visibleStations,
    isTransportMonitoringView,
    beamsOnMap,
    hcxHnaZonesChecked,
    heatMaps.flHeatMapContours,
    heatMaps.rlHeatMapContours,
    heatMapsEnabled,
    kaHnaZonesChecked,
    kuHnaZonesChecked,
    gxHnaZonesChecked,
    rlFlToggleState,
    steerableBeams.beams,
    steerableBeams.radiusNauticalMiles,
  ]);

  // Keep a WebGL context reference so we can clear contexts and not go over the browser limit
  const webGlContextRef = useRef<WebGL2RenderingContext | null>();

  const deckglOverlay = useMemo(() => {
    let overlay: any = null;

    if (map) {
      // Create Deck.gl Overlay instance
      overlay = new DeckOverlay({
        pickingRadius: 8, // 8px picking radius for artifacts. https://deck.gl/docs/api-reference/core/deck#pickingradius
        // This will run when the Device object is initialized in Deck.gl overlay, which we can use to access the underlying WebGL context
        onDeviceInitialized: device => {
          // Note: We cast as any here to prevent compiler warning, as device is typed as the parent class instead
          // of the subclass that we are using to access the gl property
          webGlContextRef.current = (device as any).gl;
        },
      });

      // Add overlay to the map
      overlay.setMap(map);
    }

    return overlay;
  }, [map]);

  // Run the cleanup for the WebGL context and the Deck.gl overlay on unmount
  useEffect(() => {
    return () => {
      if (webGlContextRef.current) {
        const extension = webGlContextRef.current.getExtension(WEBGL_LOSE_CONTEXT);
        if (extension) {
          extension.loseContext();
        }
      }
      if (deckglOverlay) {
        deckglOverlay.finalize();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deck.gl GeoJsonLayers for Beams and Heatmaps as well as hover data
  const { geoJsonLayers, hoverDataEventTarget } = useBeamGeoJsonLayers({
    data: data as FeatureCollection<MultiPolygon, Polygon>,
  });

  // Deck.gl H3HexagonLayers for GPS SA Position Accuracy
  const { selectedHexagon, gpsPositionAccuracyLayers, selectedHexagonCentroid } = useGpsSaPositionAccuracyLayers(map);

  // Adds layers to the Deck.gl Overlay
  useEffect(() => {
    if (deckglOverlay && loaded) {
      // Set Deck.gl Overlay props
      deckglOverlay.setProps({
        // Add additional Deck.gl layers here when relevant
        // Note: The order of layers is important. The last layer will be rendered on top of the previous layers.
        layers: [...geoJsonLayers, ...gpsPositionAccuracyLayers],
      });
    }
  }, [deckglOverlay, geoJsonLayers, gpsPositionAccuracyLayers, loaded]);

  return (
    <>
      {loaded && beams ? (
        <GeoJSONMapTooltip
          map={map}
          hoverDataEventTarget={hoverDataEventTarget}
          tooltipText={beamsTooltipTextWithData}
        />
      ) : (
        <></>
      )}
      {loaded && beams && heatMapsEnabled && heatMaps && Math.floor(getMapHeight(mapSettingsFtvMapSize) / 60) ? (
        <>
          <Box position={'relative'} ml={'auto'} mr="auto" width="200px" top="12px">
            {!isPlansExportView && <RlFlToggleSwitch rlFlToggleState={rlFlToggleState} />}
          </Box>
          <HeatmapLegendPanel
            data={data as FeatureCollection<MultiPolygon, Polygon>}
            linkType={rlFlToggleState === RlFlToggleStateEnum.ReturnLink ? 'rl' : 'fl'}
            numberOfBands={10}
            leftOffset={
              detailsPanelOpen &&
              (overlaysCurrentContext === OverlaysContextsEnum.FLEET_VIEW ||
                overlaysCurrentContext === OverlaysContextsEnum.FLEET_VIEW_SELECTED_TAIL)
            }
          />
        </>
      ) : (
        <></>
      )}
      <RulerPath map={map} />
      {selectedHexagon && selectedHexagonCentroid ? (
        <MapOverlayContainer
          map={map}
          id="hexagon-overlay"
          anchorLatLng={selectedHexagonCentroid}
          overlayComponent={<GpsSaPositionAccuracyTooltip selectedHexagon={selectedHexagon} />}
        />
      ) : null}
    </>
  );
};

export default MapOverlays;
